/**
 * Files out of a drop. A dropped folder is walked (where the browser offers `webkitGetAsEntry`) and
 * only the allowed file types are kept; what is left out is counted so the page can say so.
 */
export interface DroppedFiles {
  files: File[];
  /** Files of a dropped folder that were left out (a type the slot does not take). */
  skipped: number;
}

interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (ok: (file: File) => void, fail: (error: unknown) => void) => void;
  createReader?: () => { readEntries: (ok: (entries: FsEntry[]) => void, fail: (error: unknown) => void) => void };
}

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
};

async function walk(entry: FsEntry, allowed: Set<string>, out: DroppedFiles, depth: number): Promise<void> {
  if (entry.isFile && entry.file) {
    const file = await new Promise<File | null>((resolve) => entry.file!(resolve, () => resolve(null)));
    if (!file) return;
    if (allowed.has(extensionOf(file.name))) out.files.push(file);
    else out.skipped += 1;
    return;
  }
  // a limit on the depth and on the size of the result keeps a huge tree from hanging the page
  if (entry.isDirectory && entry.createReader && depth < 6) {
    const reader = entry.createReader();
    for (;;) {
      const batch = await new Promise<FsEntry[]>((resolve) => reader.readEntries(resolve, () => resolve([])));
      if (batch.length === 0) break;
      for (const child of batch) {
        if (out.files.length + out.skipped > 500) return;
        await walk(child, allowed, out, depth + 1);
      }
    }
  }
}

/**
 * Reads a drop. The entries have to be taken from the event synchronously, before anything is awaited,
 * so call this directly in the drop handler.
 */
export function readDrop(dataTransfer: DataTransfer | null | undefined, extensions: string[]): Promise<DroppedFiles> {
  const allowed = new Set(extensions.map((extension) => extension.replace(/^\./, "").toLowerCase()));
  const result: DroppedFiles = { files: [], skipped: 0 };
  if (!dataTransfer) return Promise.resolve(result);
  const items = Array.from(dataTransfer.items ?? []);
  const entries: FsEntry[] = [];
  let hasDirectory = false;
  items.forEach((item) => {
    const entry = (item as unknown as { webkitGetAsEntry?: () => FsEntry | null }).webkitGetAsEntry?.();
    if (entry) {
      entries.push(entry);
      if (entry.isDirectory) hasDirectory = true;
    }
  });
  const plain = Array.from(dataTransfer.files ?? []);
  if (!hasDirectory) {
    // no folder in it: the plain list is all there is, and the slot's own check explains each refusal
    result.files = plain;
    return Promise.resolve(result);
  }
  return (async () => {
    for (const entry of entries) await walk(entry, allowed, result, 0);
    return result;
  })();
}

/** Whether a drag carries files (and not text or a link). */
export const dragHasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes("Files");
